// ChatContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  doc, 
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  increment,
  serverTimestamp,
  limit,
  where,
  startAfter
} from 'firebase/firestore';
import { getFirestoreInstance } from '../utils/firebaseHelper';
import UserContext from './UserContext';
import { nanoid } from 'nanoid';

export const ChatContext = createContext();

const INITIAL_MESSAGES_FETCH = 10;  // How many messages to load at first
const ADDITIONAL_MESSAGES_FETCH = 6;  // How many messages to load on each "load more" action

// Cache management constants
const MESSAGE_CACHE_PREFIX = 'messages_';
const CHAT_LIST_CACHE_PREFIX = 'chatList_';

export const ChatProvider = ({ children }) => {
  const [messages, setMessages] = useState({});
  const [chatList, setChatList] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingChatList, setLoadingChatList] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState({});
  
  const { userData } = useContext(UserContext);
  const db = getFirestoreInstance();

  // Cache utilities
  const saveMessagesToCache = useCallback((chatId, messagesList) => {
    if (!userData?.userId) return;
    const cacheKey = `${MESSAGE_CACHE_PREFIX}${userData.userId}_${chatId}`;
    localStorage.setItem(cacheKey, JSON.stringify({
      timestamp: Date.now(),
      data: messagesList
    }));
  }, [userData?.userId]);

  const getCachedMessages = useCallback((chatId) => {
    if (!userData?.userId) return null;
    const cacheKey = `${MESSAGE_CACHE_PREFIX}${userData.userId}_${chatId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    try {
      const parsedCache = JSON.parse(cached);
      return parsedCache.data;
    } catch (error) {
      console.error('[CHAT] Error parsing cached messages:', error);
      localStorage.removeItem(cacheKey);
      return null;
    }
  }, [userData?.userId]);

  const saveChatListToCache = useCallback((chatList) => {
    if (!userData?.userId) return;
    const cacheKey = `${CHAT_LIST_CACHE_PREFIX}${userData.userId}`;
    localStorage.setItem(cacheKey, JSON.stringify({
      timestamp: Date.now(),
      data: chatList
    }));
  }, [userData?.userId]);

  const getCachedChatList = useCallback(() => {
    if (!userData?.userId) return null;
    const cacheKey = `${CHAT_LIST_CACHE_PREFIX}${userData.userId}`;
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;
    
    try {
      const parsedCache = JSON.parse(cached);
      return parsedCache.data;
    } catch (error) {
      console.error('[CHAT] Error parsing cached chat list:', error);
      localStorage.removeItem(cacheKey);
      return null;
    }
  }, [userData?.userId]);

  const clearMessageCache = useCallback(() => {
    if (!userData?.userId) return;
    
    // Clear all message caches for this user
    Object.keys(localStorage)
      .filter(key => key.startsWith(`${MESSAGE_CACHE_PREFIX}${userData.userId}`))
      .forEach(key => localStorage.removeItem(key));
    
    // Clear chat list cache
    localStorage.removeItem(`${CHAT_LIST_CACHE_PREFIX}${userData.userId}`);
  }, [userData?.userId]);

  // Setup listener for user's chat list using direct query on Chats collection
  useEffect(() => {
    if (!userData?.userId) return;

    console.log('[CHAT] Setting up chat list listener for user:', userData.userId);
    setLoadingChatList(true);

    // Try to load from cache first
    const cachedChatList = getCachedChatList();
    if (cachedChatList) {
      console.log('[CHAT] Using cached chat list');
      setChatList(cachedChatList);
      setLoadingChatList(false);
    }

    // Query the Chats collection directly, looking for chats where current user is a participant
    const chatsQuery = query(
      collection(db, 'Chats'),
      where('participants', 'array-contains', userData.userId)
    );

    const unsubscribe = onSnapshot(chatsQuery, async (snapshot) => {
      try {
        if (snapshot.empty) {
          console.log('[CHAT] No chats found for user:', userData.userId);
          setChatList([]);
          saveChatListToCache([]);
          setLoadingChatList(false);
          return;
        }

        // Process all the user's chats
        const userChats = await Promise.all(snapshot.docs.map(async chatDoc => {
          const chatData = chatDoc.data();
          const chatId = chatDoc.id;
          
          // Find the other participant (not the current user)
          const otherUserId = chatData.participants.find(id => id !== userData.userId);
          
          if (!otherUserId) {
            console.warn('[CHAT] No other participant found in chat:', chatId);
            return null;
          }
          
          // This is a basic chat reference, we'll fetch user details later
          return {
            chatId,
            otherUserId,
            lastMessage: chatData.lastMessage || '',
            lastUpdated: chatData.lastUpdated?.toMillis() || 0,
            timestamp: chatData.lastUpdated?.toMillis() || 0,
            unread: (chatData.unreadCount?.[userData.userId] || 0) > 0
          };
        }));
        
        // Filter out any nulls and sort by timestamp (most recent first)
        const validChats = userChats
          .filter(chat => chat !== null)
          .sort((a, b) => b.timestamp - a.timestamp);
        
        console.log(`[CHAT] Retrieved ${validChats.length} chats for user:`, userData.userId);
        setChatList(validChats);
        saveChatListToCache(validChats);
      } catch (error) {
        console.error('[CHAT] Error processing chats:', error);
        setChatList([]);
      } finally {
        setLoadingChatList(false);
      }
    }, (error) => {
      console.error('[CHAT] Error in chat list listener:', error);
      setLoadingChatList(false);
    });

    return () => {
      console.log('[CHAT] Cleaning up chat list listener');
      unsubscribe();
    };
  }, [userData?.userId, db, getCachedChatList, saveChatListToCache]);

  // Setup message listener for active chat
  useEffect(() => {
    if (!activeChat || !userData?.userId) return;

    console.log('[CHAT] Setting up message listener for:', activeChat);
    setLoading(true);

    // Try to load from cache first
    const cachedMessages = getCachedMessages(activeChat);
    if (cachedMessages) {
      console.log('[CHAT] Using cached messages for chat:', activeChat);
      setMessages(prev => ({
        ...prev,
        [activeChat]: cachedMessages
      }));
      // We still set loading to true as we'll update with real-time data
    }

    // Query the messages subcollection directly with ordering by timestamp
    const q = query(
      collection(db, 'Chats', activeChat, 'messages'),
      orderBy('timestamp', 'desc'),
      limit(INITIAL_MESSAGES_FETCH)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        if (snapshot.empty) {
          console.log('[CHAT] No messages found for chat:', activeChat);
          setMessages(prev => ({
            ...prev,
            [activeChat]: []
          }));
          saveMessagesToCache(activeChat, []);
          setHasMoreMessages(prev => ({
            ...prev,
            [activeChat]: false
          }));
          setLoading(false);
          return;
        }

        // Convert the snapshot to array of messages (sorting from oldest to newest)
        const messageList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => a.timestamp - b.timestamp);
        
        console.log(`[CHAT] Retrieved ${messageList.length} messages for chat:`, activeChat);
        
        setMessages(prev => ({
          ...prev,
          [activeChat]: messageList
        }));
        saveMessagesToCache(activeChat, messageList);
        
        // Check if there are more messages to load
        setHasMoreMessages(prev => ({
          ...prev,
          [activeChat]: snapshot.docs.length >= INITIAL_MESSAGES_FETCH
        }));
      } catch (error) {
        console.error('[CHAT] Error processing messages:', error);
        // Initialize with empty array on error
        setMessages(prev => ({
          ...prev,
          [activeChat]: []
        }));
        setHasMoreMessages(prev => ({
          ...prev,
          [activeChat]: false
        }));
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('[CHAT] Error in message listener:', error);
      setLoading(false);
    });

    // Mark messages as read when chat is opened
    if (activeChat) {
      markMessagesAsRead(activeChat);
    }

    return () => {
      console.log('[CHAT] Cleaning up message listener');
      unsubscribe();
    };
  }, [activeChat, userData?.userId, db, getCachedMessages, saveMessagesToCache]);

  // Get or create a chat
  const getOrCreateChat = useCallback(async (otherUserId) => {
    if (!userData?.userId || !otherUserId) return null;

    const chatId = [userData.userId, otherUserId].sort().join('_');
    
    try {
      const chatRef = doc(db, 'Chats', chatId);
      const chatDoc = await getDoc(chatRef);

      if (!chatDoc.exists()) {
        console.log('[CHAT] Creating new chat:', chatId);
        // Create new chat with all required fields
        await setDoc(chatRef, {
          participants: [userData.userId, otherUserId].sort(),
          created: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          lastMessage: '',
          unreadCount: {
            [userData.userId]: 0,
            [otherUserId]: 0
          }
        });
      }

      return chatId;
    } catch (error) {
      console.error('Error getting/creating chat:', error);
      return null;
    }
  }, [userData?.userId, db]);

  // Mark messages as read
  const markMessagesAsRead = useCallback(async (chatId) => {
    if (!userData?.userId || !chatId) return;

    try {
      // Update the unreadCount field directly in the Chat document
      await updateDoc(doc(db, 'Chats', chatId), {
        [`unreadCount.${userData.userId}`]: 0
      });
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [userData?.userId, db]);
  
  // Memoized setActiveChat handler
  const handleSetActiveChat = useCallback((chatId) => {
    console.log('[CHAT] Setting active chat:', chatId);
    setActiveChat(chatId);
  }, []);

  // Select chat function with memoized handler
  const selectChat = useCallback(async (otherUser) => {
    if (!otherUser?.userId || !userData?.userId) {
      console.log('[CHAT] Invalid user data for chat selection');
      return;
    }

    try {
      console.log('[CHAT] Selecting chat with user:', otherUser.userId);
      const chatId = await getOrCreateChat(otherUser.userId);
      
      if (chatId) {
        console.log('[CHAT] Setting active chat:', chatId);
        handleSetActiveChat(chatId);  // Use memoized handler
      }
    } catch (error) {
      console.error('[CHAT] Error selecting chat:', error);
    }
  }, [userData?.userId, getOrCreateChat, handleSetActiveChat]);

  // Direct chat selection - allows selecting a chat by ID
  const directSelectChat = useCallback(async (chatId, otherUser) => {
    if (!chatId || !otherUser?.userId || !userData?.userId) {
      console.log('[CHAT] Invalid data for direct chat selection');
      return;
    }

    try {
      console.log('[CHAT] Direct selecting chat:', chatId);
      
      // Check if chat exists, create it if not
      const chatRef = doc(db, 'Chats', chatId);
      const chatDoc = await getDoc(chatRef);

      if (!chatDoc.exists()) {
        console.log('[CHAT] Creating new chat:', chatId);
        // Create new chat with all required fields
        await setDoc(chatRef, {
          participants: [userData.userId, otherUser.userId].sort(),
          created: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          lastMessage: '',
          unreadCount: {
            [userData.userId]: 0,
            [otherUser.userId]: 0
          }
        });
      }

      // Set active chat
      console.log('[CHAT] Setting active chat:', chatId);
      setActiveChat(chatId);
      
      return true;
    } catch (error) {
      console.error('[CHAT] Error in direct chat selection:', error);
      return false;
    }
  }, [userData?.userId, db]);

  // Send a message
  const sendMessage = useCallback(async (content) => {
    if (!activeChat || !userData?.userId || !content.trim()) return;
  
    try {
      console.log('[CHAT] Sending message to chat:', activeChat);
      
      // Create new message object
      const newMessage = {
        content: content.trim(),
        senderId: userData.userId,
        timestamp: Date.now(),
        read: false
      };
      
      // Get chat document to find other participant
      const chatDoc = await getDoc(doc(db, 'Chats', activeChat));
      const chatData = chatDoc.data();
      const otherUserId = chatData.participants.find(id => id !== userData.userId);
  
      // Add directly to messages subcollection
      const messagesRef = collection(db, 'Chats', activeChat, 'messages');
      const docRef = await addDoc(messagesRef, newMessage);
      
      // Add the document ID to the message object
      newMessage.id = docRef.id;
      
      // Update chat document
      await updateDoc(doc(db, 'Chats', activeChat), {
        lastMessage: content.trim(),
        lastUpdated: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: increment(1)
      });
      
      // We don't need to manually update the messages state here
      // The real-time listener will handle it, avoiding duplicates
  
    } catch (error) {
      console.error('Error sending message:', error);
    }
  }, [activeChat, userData, db]);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (!activeChat || loadingMore || !hasMoreMessages[activeChat]) return;

    try {
      setLoadingMore(true);
      
      // Get the oldest message we currently have
      const currentMessages = messages[activeChat] || [];
      if (currentMessages.length === 0) {
        setLoadingMore(false);
        return;
      }
      
      // Get the timestamp of the oldest message
      const oldestMessage = currentMessages[0]; // Since messages are now ordered oldest first
      const oldestTimestamp = oldestMessage.timestamp;
      
      // Query for older messages
      const q = query(
        collection(db, 'Chats', activeChat, 'messages'),
        orderBy('timestamp', 'desc'),
        startAfter(oldestTimestamp),
        limit(ADDITIONAL_MESSAGES_FETCH)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setHasMoreMessages(prev => ({
          ...prev,
          [activeChat]: false
        }));
        setLoadingMore(false);
        return;
      }
      
      // Get older messages and add to the beginning of the array (sorted by timestamp)
      const olderMessages = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => a.timestamp - b.timestamp);
      
      setMessages(prev => {
        const updatedMessages = [...olderMessages, ...currentMessages];
        // Update cache
        saveMessagesToCache(activeChat, updatedMessages);
        return {
          ...prev,
          [activeChat]: updatedMessages
        };
      });
      
      // Check if there are more messages to load
      setHasMoreMessages(prev => ({
        ...prev,
        [activeChat]: snapshot.docs.length >= ADDITIONAL_MESSAGES_FETCH
      }));
    } catch (error) {
      console.error('Error loading more messages:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [activeChat, messages, loadingMore, hasMoreMessages, db, saveMessagesToCache]);

  // Value object with memoization for stability
  const value = React.useMemo(() => ({
    messages,
    chatList,
    activeChat,
    setActiveChat: handleSetActiveChat,
    selectChat,
    directSelectChat,
    loading,
    loadingMore,
    loadingChatList,
    sendMessage,
    loadMoreMessages,
    markMessagesAsRead,
    hasMoreMessages,
    clearMessageCache
  }), [
    messages,
    chatList, 
    activeChat, 
    handleSetActiveChat,
    selectChat,
    directSelectChat,
    loading,
    loadingMore,
    loadingChatList,
    sendMessage,
    loadMoreMessages,
    markMessagesAsRead,
    hasMoreMessages,
    clearMessageCache
  ]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;